/**
 * RolesPage — Identity & Access · Roles (Domain 1.6b rebuild, Bill 2026-05-14).
 *
 * Per identity-access-spec.md §4:
 *   - Lists /v3/config/roles with the canonical 9 roles.
 *   - Columns: Code, Display Name, Level, GroupType pill, GroupId mono trunc,
 *     Source (built-in vs custom), Status, Description.
 *   - Row click → Edit modal (typed: GroupType dropdown 4 values; GroupId
 *     disabled+cleared when GroupType=Manual; otherwise renders the live
 *     EntraGroupPicker — NOT a free-text input).
 *   - Save → POST /v3/admin/roles/upsert.
 *
 * Built fresh; not ported from the rejected v0.5.0 read-only view or the
 * ROP-teams roles editor.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useApiClient } from '@/contexts/ApiClientContext';
import { Banner, Button, Card, Pill, Spinner } from '@/components/primitives';
import { DataTable, type DataTableColumn } from '@/components/DataTable';
import { TextField, TextArea, SelectField, BoolField, NumberField } from '@/components/FormField';
import { ModalShell } from '@/components/ModalShell';
import { EntraGroupPicker } from '@/components/EntraPicker';
import { roleGrants, roleLabel } from './roleCatalog';

type GroupType = 'Teams' | 'Security' | 'MailEnabledSecurity' | 'Manual';

interface RoleRow {
  code: string;
  name: string;
  description?: string | null;
  effectiveLevel?: number;
  isSystem?: boolean;
  isActive?: boolean;
  sortOrder?: number;
  groupType?: GroupType | null;
  groupId?: string | null;
  createdAt?: string;
  updatedAt?: string;
}

interface EditDraft {
  code: string;
  name: string;
  description: string;
  effectiveLevel: number | null;
  isSystem: boolean;
  isActive: boolean;
  sortOrder: number | null;
  groupType: GroupType;
  groupId: string | null;
  groupDisplayName: string | null;
}

function rowToDraft(r: RoleRow): EditDraft {
  return {
    code: r.code,
    name: r.name,
    description: r.description ?? '',
    effectiveLevel: r.effectiveLevel ?? null,
    isSystem: r.isSystem ?? false,
    isActive: r.isActive ?? true,
    sortOrder: r.sortOrder ?? null,
    groupType: (r.groupType as GroupType) ?? 'Manual',
    groupId: r.groupId ?? null,
    groupDisplayName: null, // re-resolved via picker if the admin re-opens it
  };
}

const GROUP_TYPE_OPTIONS = [
  { value: 'Teams', label: 'Teams' },
  { value: 'Security', label: 'Security' },
  { value: 'MailEnabledSecurity', label: 'Mail-enabled Security' },
  { value: 'Manual', label: 'Manual (no Entra source)' },
];

export function RolesPage(): JSX.Element {
  const client = useApiClient();
  const [rows, setRows] = useState<ReadonlyArray<RoleRow> | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [draft, setDraft] = useState<EditDraft | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveErr, setSaveErr] = useState<string | null>(null);

  const reload = useCallback(async (): Promise<void> => {
    setError(null);
    try {
      const resp = await client.retail.config.roles();
      if (resp.success && Array.isArray(resp.data)) {
        setRows(resp.data as ReadonlyArray<RoleRow>);
      } else {
        setError(resp.error ?? 'Failed to load roles.');
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }, [client]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const columns: ReadonlyArray<DataTableColumn<RoleRow>> = useMemo(
    () => [
      // 2026-10-07 — Bill's business name first, catalog code second, and what
      // the role actually grants (derived from the gates; see roleCatalog.ts).
      {
        header: 'Role',
        cell: (r) => (
          <span>
            <strong>{roleLabel(r.code, r.name)}</strong>
            <br />
            <code className="cell-mono">{r.code}</code>
          </span>
        ),
      },
      { header: 'What it grants', cell: (r) => <span className="roles-grants">{roleGrants(r.code, r.description)}</span> },
      {
        header: 'Level',
        cell: (r) =>
          r.effectiveLevel != null ? (
            <Pill tone="muted">{String(r.effectiveLevel)}</Pill>
          ) : (
            <span className="cell-dim">—</span>
          ),
      },
      {
        header: 'GroupType',
        cell: (r) => {
          if (!r.groupType) return <span className="cell-dim">—</span>;
          if (r.groupType === 'Manual') return <Pill tone="muted">Manual</Pill>;
          return <Pill tone="info">{r.groupType}</Pill>;
        },
      },
      {
        header: 'GroupId',
        cell: (r) =>
          r.groupId ? (
            <code className="cell-mono" title={r.groupId}>
              {r.groupId.length > 8 ? `${r.groupId.slice(0, 8)}…` : r.groupId}
            </code>
          ) : (
            <span className="cell-dim">—</span>
          ),
      },
      // v0.8.13 (Bill 2026-05-15) — "Source" column dropped. The backing
      // flag (IsOrthogonal/isSystem) misleads more than informs: it shows
      // built-in only for DisclosureManager, while load-bearing hierarchy
      // roles (SuperAdmin, Admin, Officer, etc.) appear as "custom" — the
      // opposite of how an admin would read those labels. GroupType
      // already conveys the Entra sourcing model.
      {
        header: 'Status',
        cell: (r) =>
          r.isActive === false ? (
            <Pill tone="muted">inactive</Pill>
          ) : (
            <Pill tone="ok">active</Pill>
          ),
      },
      {
        header: 'Description',
        cell: (r) =>
          r.description ? (
            <span className="cell-desc">{r.description}</span>
          ) : (
            <span className="cell-dim">—</span>
          ),
      },
      {
        header: '',
        cell: (r) => (
          <Button size="sm" variant="ghost" onClick={() => setDraft(rowToDraft(r))}>
            Edit
          </Button>
        ),
      },
    ],
    [],
  );

  const save = useCallback(
    async (d: EditDraft): Promise<void> => {
      setSaving(true);
      setSaveErr(null);
      try {
        const body = {
          code: d.code,
          name: d.name.trim(),
          description: d.description.trim() || null,
          effectiveLevel: d.effectiveLevel ?? undefined,
          isSystem: d.isSystem,
          isActive: d.isActive,
          sortOrder: d.sortOrder ?? undefined,
          groupType: d.groupType,
          groupId: d.groupType === 'Manual' ? null : d.groupId,
        };
        const resp = (await (
          client.retail.admin as unknown as {
            roles: { upsert(b: unknown): Promise<{ success?: boolean; error?: string }> };
          }
        ).roles.upsert(body)) ?? { success: false, error: 'no response' };
        if (resp.success === false) {
          setSaveErr(resp.error ?? 'Save failed.');
          return;
        }
        setDraft(null);
        await reload();
      } catch (e) {
        setSaveErr(e instanceof Error ? e.message : String(e));
      } finally {
        setSaving(false);
      }
    },
    [client, reload],
  );

  if (error) {
    return <Banner tone="err">Could not load roles: {error}</Banner>;
  }
  if (!rows) {
    return <Spinner label="Loading roles…" />;
  }

  return (
    <>
      <Banner tone="info">
        Roles are given to people on the Users page (Edit → Roles). A change takes effect when the
        person next opens the app; on the server it applies within 5 minutes.
      </Banner>
      <Card title={`Roles — ${rows.length}`}>
        <DataTable<RoleRow>
          columns={columns}
          rows={rows}
          rowKey={(r) => r.code}
          emptyState={<p className="empty">No roles defined.</p>}
        />
      </Card>

      <ModalShell
        open={draft !== null}
        title={draft ? `Edit role — ${roleLabel(draft.code, draft.name)} (${draft.code})` : ''}
        onClose={() => (saving ? undefined : setDraft(null))}
        width={580}
        actions={
          draft ? (
            <>
              <Button variant="ghost" onClick={() => setDraft(null)} disabled={saving}>
                Cancel
              </Button>
              <Button onClick={() => void save(draft)} disabled={saving}>
                {saving ? 'Saving…' : 'Save'}
              </Button>
            </>
          ) : null
        }
      >
        {draft && (
          <>
            {saveErr && <Banner tone="err">{saveErr}</Banner>}
            <TextField
              label="Code"
              value={draft.code}
              onChange={() => {}}
              disabled
              monospace
              hint="Read-only after create."
            />
            <TextField
              label="Display Name"
              value={draft.name}
              onChange={(v) => setDraft({ ...draft, name: v })}
              required
            />
            <TextArea
              label="Description"
              value={draft.description}
              onChange={(v) => setDraft({ ...draft, description: v })}
              rows={3}
            />
            <NumberField
              label="Effective Level"
              value={draft.effectiveLevel}
              onChange={(v) => setDraft({ ...draft, effectiveLevel: v })}
              min={1}
              max={6}
              hint="1 (Officer) → 6 (SuperAdmin)."
            />
            <NumberField
              label="Sort Order"
              value={draft.sortOrder}
              onChange={(v) => setDraft({ ...draft, sortOrder: v })}
            />
            <SelectField
              label="Group Type"
              value={draft.groupType}
              onChange={(v) =>
                setDraft({
                  ...draft,
                  groupType: v as GroupType,
                  // Clear GroupId when flipping to Manual (UX matches server coercion).
                  groupId: v === 'Manual' ? null : draft.groupId,
                  groupDisplayName: v === 'Manual' ? null : draft.groupDisplayName,
                })
              }
              options={GROUP_TYPE_OPTIONS}
              hint="Optional. An Entra group here only produces SUGGESTIONS in Sync (an admin applies them). Roles themselves live in ROP's database and are checked from there — never from Entra group claims."
            />
            <label className="form-field">
              <span className="form-field__label">
                Group ID
                {draft.groupType !== 'Manual' && (
                  <span className="form-field__required" aria-hidden> *</span>
                )}
              </span>
              <EntraGroupPicker
                disabled={draft.groupType === 'Manual'}
                value={
                  draft.groupId
                    ? {
                        id: draft.groupId,
                        displayName: draft.groupDisplayName ?? draft.groupId,
                      }
                    : null
                }
                onChange={(g) =>
                  setDraft({
                    ...draft,
                    groupId: g?.id ?? null,
                    groupDisplayName: g?.displayName ?? null,
                  })
                }
              />
              <span className="form-field__hint">
                Picker is over live Entra (not a free-text input). Required when GroupType ≠ Manual.
              </span>
            </label>
            <BoolField
              label="Built-in (system role)"
              value={draft.isSystem}
              onChange={(v) => setDraft({ ...draft, isSystem: v })}
              hint="Built-in roles cannot be soft-deleted via the standard delete path."
            />
            <BoolField
              label="Active"
              value={draft.isActive}
              onChange={(v) => setDraft({ ...draft, isActive: v })}
            />
          </>
        )}
      </ModalShell>
    </>
  );
}
